import { db, auth } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';
import { GameState } from '../types/game';

// Get today's date key
export function getTodayKey(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

// Save completion shown flag to localStorage
export function saveCompletionShown(): void {
  try {
    const key = getTodayKey();
    localStorage.setItem(`pathways-completion-shown-${key}`, 'true');
  } catch (error) {
  }
}

// Check if completion has been shown today
export function hasCompletionBeenShown(): boolean {
  try {
    const key = getTodayKey();
    return localStorage.getItem(`pathways-completion-shown-${key}`) === 'true';
  } catch (error) {
    return false;
  }
}

// Save game state to localStorage
export function saveToLocalStorage(gameState: GameState): void {
  try {
    const key = getTodayKey();
    
    // Only save core game data, excluding UI state and functions
    const coreGameState = {
      wordsInOrder: gameState.wordsInOrder,
      words: gameState.words,
      connections: gameState.connections,
      correctPairs: gameState.correctPairs,
      correctConnectionsCount: gameState.correctConnectionsCount,
      errors: gameState.errors,
      maxErrors: gameState.maxErrors,
      selectedWord: gameState.selectedWord,
      secondSelectedWord: gameState.secondSelectedWord,
      isDragging: gameState.isDragging,
      hoveredWord: gameState.hoveredWord,
      gameCompleted: gameState.gameCompleted,
      shareMessage: gameState.shareMessage,
      fontSizeScale: gameState.fontSizeScale,
      confirmMode: gameState.confirmMode,
      dragMode: gameState.dragMode,
      lastConnection: gameState.lastConnection,
      showingSolution: gameState.showingSolution,
      solutionAnimationStep: gameState.solutionAnimationStep
    };
    
    localStorage.setItem(`pathways-game-${key}`, JSON.stringify(coreGameState));
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
}

// Load game state from localStorage
export function loadFromLocalStorage(): GameState | null {
  try {
    const key = getTodayKey();
    const saved = localStorage.getItem(`pathways-game-${key}`);
    if (saved) {
      const parsedState = JSON.parse(saved);
      // Ensure it's for today and has valid data
      if (parsedState.wordsInOrder && parsedState.wordsInOrder.length > 0) {
        return {
          ...parsedState,
          // Always recalculate positions on load
          wordPositions: {}
        };
      }
    }
  } catch (error) {
  }
  return null;
}

function sanitizeStateForFirestore(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    // Skip functions, symbols, and other non-serializable types
    if (typeof obj === 'function' || typeof obj === 'symbol') {
      return null;
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeStateForFirestore(item));
  }

  const newObj: { [key: string]: any } = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      
      // Skip functions and other non-serializable types
      if (typeof value === 'function' || typeof value === 'symbol') {
        continue;
      }
      
      if (value === undefined) {
        newObj[key] = null;
      } else {
        newObj[key] = sanitizeStateForFirestore(value);
      }
    }
  }
  return newObj;
}

// Save game state to Firestore
export async function saveToFirestore(gameState: GameState, user: User | null): Promise<boolean> {
  if (!user) {
    console.warn("Cannot save to Firestore: no authenticated user");
    return false;
  }

  try {
    const key = getTodayKey();
    
    // Only save core game data fields, exclude functions and UI state
    const coreGameState = {
      wordsInOrder: gameState.wordsInOrder,
      words: gameState.words,
      connections: gameState.connections,
      correctPairs: gameState.correctPairs,
      correctConnectionsCount: gameState.correctConnectionsCount,
      errors: gameState.errors,
      maxErrors: gameState.maxErrors,
      selectedWord: gameState.selectedWord,
      secondSelectedWord: gameState.secondSelectedWord,
      isDragging: gameState.isDragging,
      hoveredWord: gameState.hoveredWord,
      gameCompleted: gameState.gameCompleted,
      shareMessage: gameState.shareMessage,
      fontSizeScale: gameState.fontSizeScale,
      confirmMode: gameState.confirmMode,
      dragMode: gameState.dragMode,
      lastConnection: gameState.lastConnection,
      showingSolution: gameState.showingSolution,
      solutionAnimationStep: gameState.solutionAnimationStep
    };
    
    const sanitizedState = sanitizeStateForFirestore(coreGameState);
    
    await setDoc(doc(db, 'users', user.uid, 'games', key), {
      ...sanitizedState,
      lastUpdated: new Date().toISOString(),
      userId: user.uid
    });
    
    console.log("Game state saved to Firestore successfully");
    return true;
  } catch (error: any) {
    console.error("Error saving to Firestore:", error);
    
    // Handle specific Firebase errors
    if (error.code === 'permission-denied') {
      console.warn("Permission denied when saving to Firestore. User may need to re-authenticate.");
    } else if (error.code === 'unavailable') {
      console.warn("Firestore temporarily unavailable. Data saved locally only.");
    }
    
    return false;
  }
}

// Load game state from Firestore
export async function loadFromFirestore(user: User | null): Promise<GameState | null> {
  if (!user) {
    console.warn("Cannot load from Firestore: no authenticated user");
    return null;
  }

  try {
    const key = getTodayKey();
    const docRef = doc(db, 'users', user.uid, 'games', key);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      // Ensure it's for today and has valid data
      if (data.wordsInOrder && data.wordsInOrder.length > 0) {
        console.log("Game state loaded from Firestore successfully");
        return {
          ...data,
          // Always recalculate positions on load
          wordPositions: {}
        } as GameState;
      } else {
        console.log("Firestore document exists but has invalid game data");
      }
    } else {
      console.log("No saved game state found in Firestore for today");
    }
  } catch (error: any) {
    console.error("Error loading from Firestore:", error);
    
    // Handle specific Firebase errors
    if (error.code === 'permission-denied') {
      console.warn("Permission denied when loading from Firestore. User may need to re-authenticate.");
    } else if (error.code === 'unavailable') {
      console.warn("Firestore temporarily unavailable. Using local data only.");
    }
  }
  return null;
}

// REMOVED: Real-time sync is no longer needed
// Game state is now only read once on initial load and written once per move

// Merge local and cloud state (prioritize most recent)
export function mergeGameStates(local: GameState | null, cloud: GameState | null): GameState | null {
  if (!local && !cloud) return null;
  
  // Default local UI state to preserve when merging
  const defaultUIState = {
    wordPositions: {},
    toast: { message: '', show: false, type: 'error' as const },
    pendingConnection: null,
  };
  
  if (!local) {
    return {
      ...cloud!,
      ...defaultUIState
    };
  }
  
  if (!cloud) return local;

  // Compare progress - use the one with more connections or higher completion
  const localProgress = local.connections.filter(c => c.isCorrect).length;
  const cloudProgress = cloud.connections.filter(c => c.isCorrect).length;
  
  let winningState: GameState;
  
  // If cloud has more progress, use cloud
  if (cloudProgress > localProgress) {
    winningState = cloud;
  }
  // If local has more progress, use local  
  else if (localProgress > cloudProgress) {
    winningState = local;
  }
  // If same progress, use the one that's completed or has fewer errors
  else if (cloud.gameCompleted && !local.gameCompleted) {
    winningState = cloud;
  }
  else if (local.gameCompleted && !cloud.gameCompleted) {
    winningState = local;
  }
  else if (local.errors < cloud.errors) {
    winningState = local;
  }
  else {
    winningState = cloud; // Default to cloud for consistency
  }
  
  // Always preserve local UI state
  return {
    ...winningState,
    wordPositions: local.wordPositions || {},
    toast: local.toast || defaultUIState.toast,
    pendingConnection: local.pendingConnection || defaultUIState.pendingConnection,
  };
}

// Save performance data to single Firestore document per user
export async function savePerformanceToFirestore(
  user: User | null,
  performanceData: { errors: number; completedAt: string }
): Promise<boolean> {
  if (!user) {
    console.warn("Cannot save performance to Firestore: no authenticated user");
    return false;
  }
  
  // Anonymous users can still save their performance data

  try {
    const key = getTodayKey();
    const docRef = doc(db, 'userPerformance', user.uid);
    
    // Store all performance data in a single document with date keys
    await setDoc(docRef, {
      [key]: {
        ...performanceData,
        circle: performanceData.errors + 1, // 1st Circle = 0 errors, 2nd Circle = 1 error, etc.
        date: key
      },
      lastUpdated: new Date().toISOString(),
      userId: user.uid
    }, { merge: true });
    
    console.log(`Performance saved: ${performanceData.errors} errors (${performanceData.errors + 1}${getOrdinalSuffix(performanceData.errors + 1)} Circle)`);
    return true;
  } catch (error: any) {
    console.error("Error saving performance to Firestore:", error);
    
    if (error.code === 'permission-denied') {
      console.warn("Permission denied when saving performance. User may need to re-authenticate.");
    } else if (error.code === 'unavailable') {
      console.warn("Firestore temporarily unavailable. Performance not saved.");
    }
    
    return false;
  }
}

// User settings interface
interface UserSettings {
  fontSizeScale: number;
  confirmMode: boolean;
  dragMode: boolean;
}

// Default settings
const DEFAULT_SETTINGS: UserSettings = {
  fontSizeScale: 1.0,
  confirmMode: true,
  dragMode: true,
};

// Save user settings to Firestore
export async function saveUserSettings(
  user: User | null,
  settings: Partial<UserSettings>
): Promise<boolean> {
  if (!user) {
    console.warn("Cannot save user settings: no authenticated user");
    return false;
  }

  try {
    const docRef = doc(db, 'userSettings', user.uid);
    
    await setDoc(docRef, {
      ...settings,
      lastUpdated: new Date().toISOString(),
      userId: user.uid
    }, { merge: true });
    
    console.log("User settings saved to Firestore successfully");
    return true;
  } catch (error: any) {
    console.error("Error saving user settings to Firestore:", error);
    
    if (error.code === 'permission-denied') {
      console.warn("Permission denied when saving user settings. User may need to re-authenticate.");
    } else if (error.code === 'unavailable') {
      console.warn("Firestore temporarily unavailable. User settings not saved.");
    }
    
    return false;
  }
}

// Load user settings from Firestore
export async function loadUserSettings(
  user: User | null
): Promise<UserSettings | null> {
  if (!user) {
    console.warn("Cannot load user settings: no authenticated user");
    return null;
  }

  try {
    const docRef = doc(db, 'userSettings', user.uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log("User settings loaded from Firestore successfully");
      return {
        fontSizeScale: data.fontSizeScale ?? DEFAULT_SETTINGS.fontSizeScale,
        confirmMode: data.confirmMode ?? DEFAULT_SETTINGS.confirmMode,
        dragMode: data.dragMode ?? DEFAULT_SETTINGS.dragMode,
      };
    } else {
      console.log("No user settings found in Firestore, using defaults");
    }
  } catch (error: any) {
    console.error("Error loading user settings from Firestore:", error);
    
    if (error.code === 'permission-denied') {
      console.warn("Permission denied when loading user settings. User may need to re-authenticate.");
    } else if (error.code === 'unavailable') {
      console.warn("Firestore temporarily unavailable. Using default settings.");
    }
  }
  return null;
}

// Load user performance history from Firestore
export async function loadUserPerformanceHistory(
  user: User | null
): Promise<Record<string, { errors: number; completedAt: string; circle: number; date: string }> | null> {
  if (!user) {
    console.warn("Cannot load performance history: no authenticated user");
    return null;
  }
  
  // Anonymous users can still save and load their performance data

  try {
    const docRef = doc(db, 'userPerformance', user.uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      // Remove metadata fields and return only date-keyed performance data
      const { lastUpdated, userId, ...performanceData } = data;
      console.log("Performance history loaded from Firestore successfully");
      return performanceData as Record<string, { errors: number; completedAt: string; circle: number; date: string }>;
    } else {
      console.log("No performance history found in Firestore for user");
    }
  } catch (error: any) {
    console.error("Error loading performance history from Firestore:", error);
    
    if (error.code === 'permission-denied') {
      console.warn("Permission denied when loading performance history. User may need to re-authenticate.");
    } else if (error.code === 'unavailable') {
      console.warn("Firestore temporarily unavailable. Cannot load performance history.");
    }
  }
  return null;
}

// Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}